import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Package, Truck, ShieldCheck, Crown, ArrowRight, Phone, Lock, Sparkles } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import { useAuth } from '../contexts/AuthContext';

const API = `${window.location.origin}/api`;

/**
 * Wholesale & Distributor pricing page.
 * - Public visitors: see only the program teaser + apply CTA. No prices visible.
 * - Active ambassadors / distributors / admins: see the live tier table from db.business_settings.
 * Prices are CONFIDENTIAL — fetched from the auth-gated /api/me/wholesale-pricing endpoint.
 */
export default function WholesalePage() {
  const { isAuthenticated } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [locked, setLocked] = useState(true);
  const [loading, setLoading] = useState(true);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

  useEffect(() => {
    if (!isAuthenticated) { setLocked(true); setLoading(false); return; }
    axios.get(`${API}/me/wholesale-pricing`, { headers: headers() })
      .then(r => { setPricing(r.data); setLocked(false); })
      .catch(() => { setLocked(true); })
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  return (
    <main className="min-h-screen bg-[#0A0A0A]">
      <SEOHead
        title="Wholesale & Distributor Program"
        description="JS Fragrance distributor program — wholesale rates available for verified partners. Apply to unlock confidential pricing."
        url="https://jsfragrances.com/wholesale"
      />
      <BreadcrumbSchema crumbs={[{ name: 'Home', path: '/' }, { name: 'Wholesale Program', path: '/wholesale' }]} />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1C7D3F]/15 via-transparent to-[#D4AF37]/10 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-16 pb-14 text-center">
          <Package className="w-12 h-12 text-[#D4AF37] mx-auto mb-5" strokeWidth={1.4} />
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-4 font-sans">Distributor Program</p>
          <h1 className="text-4xl sm:text-5xl text-white font-light leading-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Wholesale rates for <span className="text-[#D4AF37] italic">serious volume.</span>
          </h1>
          <p className="text-white/60 text-sm sm:text-base font-sans max-w-2xl mx-auto">
            MOQ 100 pcs unlocks tiered distributor pricing. Private label and bulk shipping incentives for verified partners.
          </p>
        </div>
      </section>

      {/* Locked teaser OR live pricing */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14 space-y-12">
        {loading ? (
          <p className="text-white/40 text-sm font-sans py-12 text-center">Loading…</p>
        ) : locked ? (
          <LockedTeaser isAuthenticated={isAuthenticated} />
        ) : (
          <LivePricing pricing={pricing} />
        )}

        {/* Ambassador vs Distributor — always public */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-[#0F0F0F] border border-[#D4AF37]/30 p-6">
            <Crown className="w-7 h-7 text-[#D4AF37] mb-3" strokeWidth={1.4} />
            <h3 className="text-lg text-white font-medium font-sans mb-2">Ambassador</h3>
            <p className="text-white/60 text-sm font-sans mb-4">Buy a Tester Kit to qualify. Sell single bottles. Earn per-bottle commission + ₱200 per referral.</p>
            <Link to="/ambassador-apply" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#D4AF37] hover:text-[#F3E5AB] font-sans" data-testid="apply-ambassador-cta">
              Apply as Ambassador <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="bg-[#0F0F0F] border border-[#1C7D3F]/40 p-6">
            <Truck className="w-7 h-7 text-[#1C7D3F]" strokeWidth={1.4} />
            <h3 className="text-lg text-white font-medium font-sans mt-3 mb-2">Distributor</h3>
            <p className="text-white/60 text-sm font-sans mb-4">Order 100+ pcs to qualify. Private label, co-branding, bulk shipping incentives, dedicated account manager.</p>
            <a href="https://m.me/jsfragranceofficial" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#1C7D3F] hover:text-[#2E9B50] font-sans" data-testid="contact-distributor-cta">
              <Phone className="w-3.5 h-3.5" /> Talk to JS directly
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

/* ─── Public lock state — NO prices shown ─── */
function LockedTeaser({ isAuthenticated }) {
  return (
    <div className="bg-[#0F0F0F] border border-[#D4AF37]/30 p-8 sm:p-10 text-center" data-testid="wholesale-locked">
      <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-5">
        <Lock className="w-5 h-5 text-[#D4AF37]" />
      </div>
      <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-2 font-sans">Confidential Partner Pricing</p>
      <h2 className="text-2xl sm:text-3xl text-white font-light mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        Wholesale tiers are <span className="text-[#D4AF37] italic">verified-partner only.</span>
      </h2>
      <p className="text-white/60 text-sm font-sans max-w-md mx-auto mb-7">
        We don&apos;t publish wholesale rates publicly to protect our network of dealers. Apply or sign in as a verified ambassador / distributor to unlock the full tier card.
      </p>

      <div className="grid sm:grid-cols-3 gap-3 max-w-xl mx-auto mb-8 text-left">
        <Perk label="Up to 14% off retail" />
        <Perk label="MOQ from 100 pcs" />
        <Perk label="Private label option" />
      </div>

      <div className="flex items-center justify-center gap-3 flex-wrap">
        <Link to="/ambassador-apply" className="inline-flex items-center gap-2 px-5 py-3 bg-[#D4AF37] text-black uppercase tracking-[0.22em] text-xs font-sans hover:bg-[#E5C158] transition-colors" data-testid="locked-apply-btn">
          <Sparkles className="w-3.5 h-3.5" /> Apply as Ambassador
        </Link>
        {!isAuthenticated && (
          <Link to="/login" className="inline-flex items-center gap-2 px-5 py-3 border border-white/15 text-white/80 uppercase tracking-[0.22em] text-xs font-sans hover:border-white/40 hover:text-white transition-colors" data-testid="locked-login-btn">
            Sign in
          </Link>
        )}
      </div>
    </div>
  );
}

function Perk({ label }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/5 px-4 py-3 text-xs text-white/70 font-sans">
      <span className="text-[#1C7D3F] mr-1.5">◆</span>{label}
    </div>
  );
}

/* ─── Authed view — live confidential pricing ─── */
function LivePricing({ pricing }) {
  if (!pricing) return null;
  const categories = pricing.category_pricing || [];
  return (
    <>
      {categories.length === 0 && (
        <p className="text-white/40 text-sm font-sans py-12 text-center">
          No pricing categories configured yet. Admin must add them in the Business Configuration tab.
        </p>
      )}
      {categories.map((c, i) => (
        <div key={i} data-testid={`category-pricing-${i}`}>
          <div className="flex items-end justify-between gap-4 mb-4 border-b border-white/10 pb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans">{c.size || 'Wholesale'}</p>
              <h2 className="text-2xl text-white font-medium mt-1 font-sans">{c.category}</h2>
            </div>
            <p className="text-white/40 text-xs font-sans hidden sm:block">All prices in PHP — confidential</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(c.tiers || []).map((t, ti) => (
              <TierCard key={ti} t={t} />
            ))}
          </div>
        </div>
      ))}

      {(pricing.wholesale_terms || []).length > 0 && (
        <div className="bg-[#141414] border border-white/10 p-6 sm:p-8" data-testid="wholesale-terms">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="text-sm uppercase tracking-[0.22em] text-white font-sans">Terms &amp; Conditions</h3>
          </div>
          <ul className="space-y-2 text-sm text-white/70 font-sans">
            {pricing.wholesale_terms.map((t, i) => (
              <li key={i} className="flex items-start gap-2.5"><span className="text-[#D4AF37] mt-1">◆</span><span>{t}</span></li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function TierCard({ t }) {
  const qty = t.max_qty ? `${t.min_qty} – ${t.max_qty}` : `${t.min_qty}+`;
  return (
    <div className="bg-[#141414] border border-white/10 hover:border-[#D4AF37]/40 transition-colors p-5">
      <div className="flex items-baseline justify-between mb-1">
        <p className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-sans">{t.label}</p>
        <p className="text-2xl text-white font-medium font-sans">₱{Number(t.price).toFixed(2)}</p>
      </div>
      <p className="text-white/50 text-xs font-sans">{qty} pcs · {t.note}</p>
    </div>
  );
}
